import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { infospyService } from '../services/api';
import '../styles/InfoSpy.css';

// Timeouts HTTP no cliente: com fila muito grande a API pode demorar a responder;
// valores baixos causam erro e nova consulta joga o CPF ao fim da fila de novo.
const INFOSPY_CONSULTAR_TIMEOUT_MS = 180000;
const INFOSPY_STATUS_TIMEOUT_MS = 120000;

const ConsultaInfoSpy = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cpf, setCpf] = useState('');
  const [loading, setLoading] = useState(false);
  const [consultaAtual, setConsultaAtual] = useState(null);
  const [error, setError] = useState('');
  const [pollingInterval, setPollingInterval] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  // CPF mask: 000.000.000-00
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  const handleCpfChange = (e) => {
    setCpf(formatCPF(e.target.value));
    setError('');
  };

  const startPolling = (cpfLimpo) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    const interval = setInterval(async () => {
      try {
        const response = await infospyService.getStatus(cpfLimpo, {
          timeout: INFOSPY_STATUS_TIMEOUT_MS
        });
        if (response.success && response.data) {
          setConsultaAtual(response.data);

          if (['completed', 'error', 'sem_cadastro'].includes(response.data.status)) {
            clearInterval(interval);
            setPollingInterval(null);
          }
        }
      } catch (err) {
        console.error('Erro no polling:', err);
      }
    }, 2000); // Atualiza a cada 2 segundos para posição em tempo real

    setPollingInterval(interval);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cpfLimpo = cpf.replace(/\D/g, '');
    if (cpfLimpo.length !== 11) {
      setError('CPF deve conter 11 dígitos');
      return;
    }

    setLoading(true);
    setError('');
    setConsultaAtual(null);

    try {
      const response = await infospyService.consultar(cpfLimpo, {
        timeout: INFOSPY_CONSULTAR_TIMEOUT_MS
      });

      if (response.success && response.data) {
        setConsultaAtual(response.data);

        if (['pending', 'processing'].includes(response.data.status)) {
          startPolling(cpfLimpo);
        }
      }
    } catch (err) {
      const status = err.response?.status;
      const respData = err.response?.data;

      if (status === 429 && respData?.consulta) {
        setError(respData?.error || 'Você já possui uma consulta em andamento');
        setConsultaAtual(respData.consulta);
        if (['pending', 'processing'].includes(respData.consulta.status)) {
          startPolling(respData.consulta.cpf);
        }
      } else {
        setError(respData?.error || respData?.message || 'Erro ao iniciar consulta');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    async function loadConsultaAtiva() {
      try {
        const response = await infospyService.getConsultaAtiva({
          timeout: INFOSPY_STATUS_TIMEOUT_MS
        });
        if (cancelled) return;

        if (response.success && response.data) {
          setConsultaAtual(response.data);
          if (['pending', 'processing'].includes(response.data.status)) {
            startPolling(response.data.cpf);
          }
        }
      } catch (err) {
        // silêncio: não bloquear a página se falhar
        console.error('Erro ao buscar consulta ativa:', err);
      }
    }

    loadConsultaAtiva();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const getStatusConfig = (status) => {
    const configs = {
      pending: { 
        label: 'Aguardando', 
        icon: '⏳', 
        class: 'infospy-status-pending',
        step: 0
      },
      processing: { 
        label: 'Processando', 
        icon: '🔄', 
        class: 'infospy-status-processing',
        step: 1
      },
      completed: { 
        label: 'Concluído', 
        icon: '✅', 
        class: 'infospy-status-completed',
        step: 2
      },
      sem_cadastro: { 
        label: 'Sem Cadastro', 
        icon: '⚠️', 
        class: 'infospy-status-sem_cadastro',
        step: -1
      },
      error: { 
        label: 'Erro', 
        icon: '❌', 
        class: 'infospy-status-error',
        step: -1
      }
    };
    return configs[status] || configs.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const canViewHistorico = ['admin', 'diretor', 'supervisor', 'supervisor_bko', 'backoffice', 'vendedor'].includes(
    user?.role
  );

  // Mesmos perfis que o backend envia como `prioridade: true` na API InfoSpy (READMEinfo.md)
  const temFilaPrioritariaInfospy = ['admin', 'diretor', 'backoffice'].includes(user?.role);

  // Modal de imagem ampliada
  const ImageModal = () => {
    if (!selectedImage) return null;

    return (
      <div 
        className="infospy-modal-overlay"
        onClick={() => setSelectedImage(null)}
      >
        <div 
          className="infospy-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            className="infospy-modal-close"
            onClick={() => setSelectedImage(null)}
          >
            ✕
          </button>
          <img 
            src={selectedImage.url} 
            alt={selectedImage.filename}
            className="infospy-modal-image"
          />
          <div className="infospy-modal-filename">{selectedImage.filename}</div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="infospy-container">
        {/* Header */}
        <div className="infospy-header">
          <div className="infospy-header-left">
            <h1 className="infospy-title">
              <div className="infospy-title-icon">🔍</div>
              Consulta InfoSpy
            </h1>
            <p className="infospy-subtitle">
              Consulte informações de CPF através da API InfoSpy e visualize os resultados
              {temFilaPrioritariaInfospy && (
                <span className="infospy-subtitle-priority">
                  {' '}
                  · Fila prioritária (administrador, diretor ou BKO)
                </span>
              )}
            </p>
          </div>
          <div className="infospy-header-right">
            {canViewHistorico && (
              <button
                className="infospy-btn infospy-btn-outline"
                onClick={() => navigate('/infospy/historico')}
              >
                📊 Ver Histórico
              </button>
            )}
          </div>
        </div>

        {/* Quick Info Cards */}
        <div className="infospy-quick-info">
          <div className="infospy-info-card">
            <div className="infospy-info-card-icon">🔎</div>
            <div className="infospy-info-card-title">Consulta Rápida</div>
            <div className="infospy-info-card-value">CPF</div>
          </div>
          <div className="infospy-info-card">
            <div className="infospy-info-card-icon">📷</div>
            <div className="infospy-info-card-title">Imagens</div>
            <div className="infospy-info-card-value">Automáticas</div>
          </div>
          <div className="infospy-info-card">
            <div className="infospy-info-card-icon">💾</div>
            <div className="infospy-info-card-title">Armazenamento</div>
            <div className="infospy-info-card-value">Local</div>
          </div>
        </div>

        {/* Consultation Form */}
        <div className="infospy-card">
          <div className="infospy-card-header">
            <div className="infospy-card-header-icon">📝</div>
            <span className="infospy-card-header-title">Nova Consulta</span>
          </div>
          <div className="infospy-card-body">
            <form onSubmit={handleSubmit}>
              <div className="infospy-form">
                <div className="infospy-form-group">
                  <label className="infospy-form-label">CPF para consulta</label>
                  <input
                    type="text"
                    value={cpf}
                    onChange={handleCpfChange}
                    placeholder="000.000.000-00"
                    maxLength={14}
                    className="infospy-form-input"
                    disabled={loading || (consultaAtual && ['pending', 'processing'].includes(consultaAtual.status))}
                  />
                </div>
                <button
                  type="submit"
                  disabled={
                    loading ||
                    cpf.replace(/\D/g, '').length !== 11 ||
                    (consultaAtual && ['pending', 'processing'].includes(consultaAtual.status))
                  }
                  className="infospy-btn infospy-btn-primary"
                >
                  {loading ? (
                    <>
                      <div className="infospy-spinner"></div>
                      Consultando...
                    </>
                  ) : (
                    <>
                      {consultaAtual && ['pending', 'processing'].includes(consultaAtual.status)
                        ? '⏳ Consulta em andamento'
                        : '🔍 Consultar CPF'}
                    </>
                  )}
                </button>
              </div>
            </form>

            {error && (
              <div className="infospy-error">
                <span className="infospy-error-icon">❌</span>
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>

        {/* Pending/Processing Status - Real-time queue position */}
        {consultaAtual && ['pending', 'processing'].includes(consultaAtual.status) && (
          <div className="infospy-card">
            <div className="infospy-queue-status">
              <div className="infospy-queue-icon">
                {consultaAtual.status === 'pending' ? '⏳' : '🔄'}
              </div>
              <div className="infospy-queue-info">
                <div className="infospy-queue-title">
                  {consultaAtual.status === 'pending' ? 'Aguardando na Fila' : 'Processando'}
                </div>
                {temFilaPrioritariaInfospy && consultaAtual.status === 'pending' && (
                  <div className="infospy-queue-priority-note">
                    Esta consulta entrou na fila de prioridade da API.
                  </div>
                )}
                {consultaAtual.posicao_fila > 0 && (
                  <div className="infospy-queue-position">
                    <span className="infospy-queue-position-label">Posição na fila:</span>
                    <span className="infospy-queue-position-number">#{consultaAtual.posicao_fila}</span>
                  </div>
                )}
                <div className="infospy-queue-updating">
                  <div className="infospy-pulse-dot"></div>
                  Atualizando em tempo real...
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progress Indicator */}
        {consultaAtual && ['pending', 'processing'].includes(consultaAtual.status) && (
          <div className="infospy-card">
            <div className="infospy-progress">
              <div className="infospy-progress-steps">
                <div className={`infospy-progress-step ${consultaAtual.status === 'pending' ? 'active' : 'completed'}`}>
                  <div className="infospy-progress-step-icon">
                    {consultaAtual.status === 'pending' ? '⏳' : '✅'}
                  </div>
                  <span className="infospy-progress-step-label">Adicionado à fila</span>
                </div>
                <div className={`infospy-progress-line ${consultaAtual.status === 'processing' ? '' : 'completed'}`}></div>
                <div className={`infospy-progress-step ${consultaAtual.status === 'processing' ? 'active' : ''}`}>
                  <div className="infospy-progress-step-icon">🔄</div>
                  <span className="infospy-progress-step-label">Processando</span>
                </div>
                <div className="infospy-progress-line"></div>
                <div className="infospy-progress-step">
                  <div className="infospy-progress-step-icon">📷</div>
                  <span className="infospy-progress-step-label">Concluído</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Result */}
        {consultaAtual && (
          <div className="infospy-card infospy-result">
            <div className="infospy-result-header">
              <div className="infospy-cpf-display">
                <span className={`infospy-status ${getStatusConfig(consultaAtual.status).class}`}>
                  {getStatusConfig(consultaAtual.status).icon} {getStatusConfig(consultaAtual.status).label}
                </span>
                <span className="infospy-cpf-number">
                  {consultaAtual.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                </span>
              </div>
              {consultaAtual.posicao_fila > 0 && ['pending', 'processing'].includes(consultaAtual.status) && (
                <div className="infospy-queue-badge">
                  <span className="infospy-queue-badge-icon">📍</span>
                  Posição: {consultaAtual.posicao_fila}
                </div>
              )}
            </div>

            {/* Meta Info */}
            <div className="infospy-card-body">
              <div className="infospy-meta">
                <div className="infospy-meta-item">
                  <span className="infospy-meta-label">ID da Consulta</span>
                  <span className="infospy-meta-value">#{consultaAtual.id}</span>
                </div>
                <div className="infospy-meta-item">
                  <span className="infospy-meta-label">Data/Hora</span>
                  <span className="infospy-meta-value">{formatDate(consultaAtual.created_at)}</span>
                </div>
                {consultaAtual.error_message && (
                  <div className="infospy-meta-item">
                    <span className="infospy-meta-label">Mensagem</span>
                    <span className="infospy-meta-value" style={{ color: '#ff4444' }}>
                      {consultaAtual.error_message}
                    </span>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {consultaAtual.status === 'error' && consultaAtual.error_message && (
                <div className="infospy-error" style={{ marginTop: '1rem' }}>
                  <span className="infospy-error-icon">❌</span>
                  <span>{consultaAtual.error_message}</span>
                </div>
              )}

              {/* Sem Cadastro Warning */}
              {consultaAtual.status === 'sem_cadastro' && (
                <div className="infospy-warning">
                  <span className="infospy-warning-icon">⚠️</span>
                  <div>
                    <strong>CPF não encontrado</strong>
                    <p style={{ margin: '0.25rem 0 0', opacity: 0.9 }}>
                      O CPF informado não possui cadastro no sistema ou o cliente já possui instalação realizada.
                    </p>
                  </div>
                </div>
              )}

              {/* Images Section */}
              {consultaAtual.status === 'completed' && consultaAtual.imagens?.length > 0 && (
                <div className="infospy-images-section">
                  <h3 className="infospy-images-title">
                    📷 Imagens
                    <span className="infospy-images-count">{consultaAtual.imagens.length}</span>
                  </h3>
                  <div className="infospy-images-grid">
                    {consultaAtual.imagens.map((img) => (
                      <div 
                        key={img.id} 
                        className="infospy-image-card"
                        onClick={() => setSelectedImage({ 
                          url: infospyService.getImageUrl(consultaAtual.id, img.filename), 
                          filename: img.filename 
                        })}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="infospy-image-wrapper">
                          <img
                            src={infospyService.getImageUrl(consultaAtual.id, img.filename)}
                            alt={img.filename}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzFhMWFhYSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZW0gbsOjbyBlbmNvbnRyYWRhPC90ZXh0Pjwvc3ZnPg==';
                            }}
                          />
                          <div className="infospy-image-overlay">
                            <span className="infospy-image-overlay-text">🔍 Clique para ampliar</span>
                          </div>
                        </div>
                        <div className="infospy-image-name">{img.filename}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Images for Completed */}
              {consultaAtual.status === 'completed' && !consultaAtual.imagens?.length && (
                <div className="infospy-warning" style={{ marginTop: '1rem' }}>
                  <span className="infospy-warning-icon">📷</span>
                  <div>
                    <strong>Nenhuma imagem disponível</strong>
                    <p style={{ margin: '0.25rem 0 0', opacity: 0.9 }}>
                      A consulta foi concluída, mas não há imagens disponíveis para este CPF.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      <ImageModal />
    </Layout>
  );
};

export default ConsultaInfoSpy;